import { ApplyResult, PrepareResult, ProcessResult, StageStatus } from './types'

export function getStatusItems(
  status: StageStatus,
  additionalItems: Array<{
    label: string
    value: string | number | boolean | null | undefined
  }> | null,
): Array<{
  label: string
  value: string | number | boolean | null | undefined
}> {
  const items: Array<{
    label: string
    value: string | number | boolean | null | undefined
  }> = [
    { label: 'Status', value: status.status },
    { label: 'Completed', value: status.flow_status?.completed ? 'Yes' : 'No' },
    { label: 'Controller ID', value: status.controller_id },
  ]

  // Add flow status details if available
  if (status.flow_status) {
    const flowStatus = status.flow_status
    items.push({
      label: 'Create Time',
      value: new Date(flowStatus.details.create_time).toLocaleString(),
    })

    // Add dependant jobs statistics
    if (flowStatus.dependant_jobs.total > 0) {
      items.push(
        {
          label: 'Dependant Jobs',
          value: `${flowStatus.dependant_jobs.completed}/${flowStatus.dependant_jobs.total} completed`,
        },
        { label: 'Dependant Failed', value: flowStatus.dependant_jobs.failed },
        {
          label: 'Dependant Pending',
          value: flowStatus.dependant_jobs.pending,
        },
      )
    }

    // Add worker jobs statistics
    if (flowStatus.worker_jobs.total > 0) {
      items.push(
        {
          label: 'Worker Jobs',
          value: `${flowStatus.worker_jobs.completed}/${flowStatus.worker_jobs.total} completed`,
        },
        { label: 'Worker Failed', value: flowStatus.worker_jobs.failed },
        { label: 'Worker Pending', value: flowStatus.worker_jobs.pending },
      )
    }
  } else {
    // Fallback to basic progress if no flow status
    items.push({ label: 'Progress', value: status.progress || 0 })
  }

  if (status.message) {
    items.push({ label: 'Message', value: status.message })
  }

  if (status.errors && status.errors.length > 0) {
    items.push({ label: 'Errors', value: status.errors.join(', ') })
  }

  if (additionalItems) {
    items.push(...additionalItems)
  }

  return items
}

export function getJobProgressSummary(flowStatus: any): string {
  if (!flowStatus) return 'No flow status available'

  const { dependant_jobs, worker_jobs } = flowStatus
  const totalJobs = dependant_jobs.total + worker_jobs.total
  const completedJobs = dependant_jobs.completed + worker_jobs.completed
  const failedJobs = dependant_jobs.failed + worker_jobs.failed

  if (totalJobs === 0) return 'No jobs to process'

  const progress = Math.round((completedJobs / totalJobs) * 100)
  return `${completedJobs}/${totalJobs} jobs completed (${progress}%)${failedJobs > 0 ? `, ${failedJobs} failed` : ''}`
}

export function getPrepareResultItems(result: PrepareResult): Array<{
  label: string
  value: string | number | boolean | null | undefined
}> {
  return [
    { label: 'Controller ID', value: result.controller_id },
    { label: 'Tree ID', value: result.tree_id },
    { label: 'Step', value: result.step },
    { label: 'Status', value: result.status },
    { label: 'Total SVG Count', value: result.result?.total_svg_count || 0 },
    { label: 'Node Count', value: result.result?.node_count || 0 },
    { label: 'Message', value: result.message || 'N/A' },
  ]
}

export function getProcessResultItems(result: ProcessResult): Array<{
  label: string
  value: string | number | boolean | null | undefined
}> {
  return [
    { label: 'Controller ID', value: result.controller_id },
    { label: 'Tree ID', value: result.tree_id },
    { label: 'Step 1 Controller ID', value: result.step1_controller_id },
    { label: 'Step', value: result.step },
    { label: 'Status', value: result.status },
    { label: 'Processed SVGs', value: result.result?.processed_svg_count || 0 },
    { label: 'Message', value: result.message || 'N/A' },
  ]
}

export function getApplyResultItems(result: ApplyResult): Array<{
  label: string
  value: string | number | boolean | null | undefined
}> {
  return [
    { label: 'Controller ID', value: result.controller_id },
    { label: 'Tree ID', value: result.tree_id },
    { label: 'Step 2 Controller ID', value: result.step2_controller_id },
    { label: 'Step', value: result.step },
    { label: 'Status', value: result.status },
    {
      label: 'Applied Changes',
      value: result.result?.applied_changes_count || 0,
    },
    { label: 'Message', value: result.message || 'N/A' },
  ]
}
